<template>
    <div
        ref="root"
        :data-cromi-recovery-required="
            recoveryRequired
                ? recoveryUsable
                    ? 'ready'
                    : 'pending'
                : undefined
        "
        :style="{ minHeight }">
        <slot v-if="isVisible" />
        <slot
            v-else
            name="fallback" />
    </div>
</template>

<script setup lang="ts">
const props = withDefaults(
    defineProps<{
        rootMargin?: string
        minHeight?: string
        recoveryRequired?: boolean
        recoveryReady?: boolean
        recoveryTransitioning?: boolean
    }>(),
    {
        rootMargin: '600px',
        minHeight: '1px',
    }
)

const emit = defineEmits<{
    visible: []
}>()

const root = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const recoveryUsable = computed(
    () => isVisible.value && props.recoveryReady && !props.recoveryTransitioning
)
// The page owns data readiness; mounting a placeholder is not useful content.
watch(
    recoveryUsable,
    async (ready) => {
        if (!ready || !props.recoveryRequired) return
        const contentUrl = location.href
        await nextTick()
        if (
            recoveryUsable.value &&
            location.href === contentUrl &&
            root.value?.isConnected
        )
            window.__cromiRecovery?.capture()
    },
    { flush: 'post' }
)
let observer: IntersectionObserver | null = null

function markVisible() {
    if (isVisible.value) return

    isVisible.value = true
    emit('visible')
    observer?.disconnect()
}

onMounted(() => {
    if (!root.value || !('IntersectionObserver' in window)) {
        markVisible()
        return
    }

    observer = new IntersectionObserver(
        ([entry]) => {
            if (entry?.isIntersecting) {
                markVisible()
            }
        },
        { rootMargin: props.rootMargin }
    )

    observer.observe(root.value)
})

onBeforeUnmount(() => {
    observer?.disconnect()
})
</script>
